import { RANK_NAMES, RankName } from "poker-odds/lib/utils";
import { rankHand } from "poker-odds/lib/rank";
import { createDeck, deal } from "poker-odds/lib/deck";
import { SeatPosition, CardType } from "types";

export function calculatePokerOdds({
  players,
  dealerCards,
  maxTime, // in ms
  maxIterations,
}: {
  players: Array<{ seatIndex: SeatPosition; playerCards: CardType[] }>;
  dealerCards: CardType[];
  maxTime?: number;
  maxIterations?: number;
}) {
  const hands = players.map(({ playerCards }) =>
    transformCardsToInput(playerCards),
  );
  const board = transformCardsToInput(
    dealerCards.filter((card) => card !== CardType.CARD_BACK),
  );

  if (hands.length < 2) return undefined;

  try {
    const results = calculateEquity(hands, board, maxTime, maxIterations);

    return {
      players: players.map((player, index) => {
        const result = results[index];
        return { ...player, result };
      }),
      dealerCards: dealerCards,
    };
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

// Matching to a map is faster than splitting or using regex
const CARD_MAPPING: Record<CardType, string> = {
  [CardType.CLUB_1]: "Ac",
  [CardType.CLUB_2]: "2c",
  [CardType.CLUB_3]: "3c",
  [CardType.CLUB_4]: "4c",
  [CardType.CLUB_5]: "5c",
  [CardType.CLUB_6]: "6c",
  [CardType.CLUB_7]: "7c",
  [CardType.CLUB_8]: "8c",
  [CardType.CLUB_9]: "9c",
  [CardType.CLUB_10]: "Tc",
  [CardType.CLUB_11]: "Jc",
  [CardType.CLUB_12]: "Qc",
  [CardType.CLUB_13]: "Kc",
  [CardType.CLUB_14]: "Ac",
  [CardType.DIAMOND_1]: "Ad",
  [CardType.DIAMOND_2]: "2d",
  [CardType.DIAMOND_3]: "3d",
  [CardType.DIAMOND_4]: "4d",
  [CardType.DIAMOND_5]: "5d",
  [CardType.DIAMOND_6]: "6d",
  [CardType.DIAMOND_7]: "7d",
  [CardType.DIAMOND_8]: "8d",
  [CardType.DIAMOND_9]: "9d",
  [CardType.DIAMOND_10]: "Td",
  [CardType.DIAMOND_11]: "Jd",
  [CardType.DIAMOND_12]: "Qd",
  [CardType.DIAMOND_13]: "Kd",
  [CardType.DIAMOND_14]: "Ad",
  [CardType.HEART_1]: "Ah",
  [CardType.HEART_2]: "2h",
  [CardType.HEART_3]: "3h",
  [CardType.HEART_4]: "4h",
  [CardType.HEART_5]: "5h",
  [CardType.HEART_6]: "6h",
  [CardType.HEART_7]: "7h",
  [CardType.HEART_8]: "8h",
  [CardType.HEART_9]: "9h",
  [CardType.HEART_10]: "Th",
  [CardType.HEART_11]: "Jh",
  [CardType.HEART_12]: "Qh",
  [CardType.HEART_13]: "Kh",
  [CardType.HEART_14]: "Ah",
  [CardType.SPADE_1]: "As",
  [CardType.SPADE_2]: "2s",
  [CardType.SPADE_3]: "3s",
  [CardType.SPADE_4]: "4s",
  [CardType.SPADE_5]: "5s",
  [CardType.SPADE_6]: "6s",
  [CardType.SPADE_7]: "7s",
  [CardType.SPADE_8]: "8s",
  [CardType.SPADE_9]: "9s",
  [CardType.SPADE_10]: "Ts",
  [CardType.SPADE_11]: "Js",
  [CardType.SPADE_12]: "Qs",
  [CardType.SPADE_13]: "Ks",
  [CardType.SPADE_14]: "As",
  [CardType.CARD_BACK]: "",
};

function transformCardsToInput(playerCards: CardType[]) {
  return playerCards.map((card) => CARD_MAPPING[card]);
}

function calculateEquity(
  hands: string[][],
  board: string[],
  maxTime = 200, // in ms
  maxIterations = 100000,
) {
  let results = hands.map((hand) => ({
    hand,
    count: 0,
    wins: 0,
    ties: 0,
    winsWithTies: 0,
    handChances: RANK_NAMES.map((name) => ({ name, count: 0 })),
  }));
  if (board.length === 5) {
    results = analyse(results, board);
  } else if (board.length >= 3) {
    const deck = createDeck(board.concat(...hands));
    for (let i = 0; i !== deck.length; i++) {
      if (board.length === 4) {
        results = analyse(results, board.concat(deck[i]));
        continue;
      }
      for (let j = 0; j !== deck.length; j++) {
        if (i >= j) continue;
        results = analyse(results, board.concat([deck[i], deck[j]]));
      }
    }
  } else {
    // Date.now() is faster than performance.now()
    const targetStopTimestamp = Date.now() + maxTime;

    for (
      let i = 0;
      i !== maxIterations && Date.now() <= targetStopTimestamp;
      i++
    ) {
      const randomCards = deal(
        ([] as string[]).concat(...hands),
        5 - board.length,
      );
      results = analyse(results, board.concat(randomCards));
    }
  }
  const maxWins = Math.max(...results.map((hand) => hand.winsWithTies));
  return results.map((hand) => ({
    ...hand,
    favourite: hand.winsWithTies === maxWins,
    equity: Number(
      (Math.round((hand.winsWithTies / hand.count) * 10000) / 100).toFixed(2),
    ),
  }));
}

function analyse(
  results: {
    hand: string[];
    count: number;
    wins: number;
    ties: number;
    winsWithTies: number;
    handChances: {
      name: RankName;
      count: number;
    }[];
  }[],
  board: string[],
) {
  const ranks = results.map((result) => {
    return rankHand(result.hand.concat(board));
  });
  const bestRank = ranks.slice(0).sort().reverse()[0];
  const winnerRanks = ranks.filter((rank) => rank === bestRank);
  const tie = winnerRanks.length > 1;
  for (let i = 0; i !== results.length; i++) {
    if (ranks[i] === bestRank) {
      if (tie) {
        results[i].ties++;
        results[i].winsWithTies += 1 / winnerRanks.length;
      } else {
        results[i].wins++;
        results[i].winsWithTies++;
      }
    }
    results[i].count++;
    results[i].handChances[parseInt(ranks[i][0])].count++;
  }
  return results;
}
